import {
  ActionCreatorWithoutPayload,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
  SliceCaseReducers
} from '@reduxjs/toolkit'
import {mergeDeepRight} from 'ramda'

import {
  persistCognitoUserTierOnCookie,
  removeUnnecessaryCookies
} from '../business/persistData'
import {
  AuthenticatedUser,
  AuthenticationFlows,
  ClaimsType,
  ShowPasswordlessLoginPayload,
  UserAction
} from '../types/Auth'
import {CognitoError, User, UserAttributes, UserPoolId} from '../types/Cognito'
import {
  makeSocialLogin,
  updateUserAttributes,
  upgradeUserToPlus
} from './thunks'

export type AuthStep = 'SignInStep'

export type SusiHistoryState = {
  active: boolean
  analyticsData?: Record<string, unknown>
}

/**
 * The Auth Redux state Slice
 */
export type StateType = {
  /** Authentication errors */
  error: {
    errorType?: string
    errorMessage?: string | CognitoError
  }
  lastAction?: string
  /** Handles the UI state to manage the passwordless login modal visibility */
  isPasswordlessLoginVisible: boolean
  /** ??? */
  status: string
  /** The user's phone number */
  userPhoneNumber: string | null | undefined
  /** Handles the UI state to manage the "resending code" message visibility */
  isUserResending: boolean
  /** The authenticated user id */
  userId: string | null | undefined
  /** Which flow the user is using to login/signup */
  flow: AuthenticationFlows | null
  /** The hotelId the user clicked on the unlock flow */
  unlockHotelId?: string | null
  /** Which component the user triggered login/signup */
  sourceComponent?: string | null
  /** Is the user privileged */
  shouldSeeOffersUnlocked: boolean
  /** Is the user authenticated */
  isAuthenticated: boolean
  /** The user */
  user: User | null
  /** The loading state when authenticating the user */
  isLoading: boolean
  /** Profile picture URL from Amplify Storage.get */
  profilePictureUrl?: string
  userPoolId: UserPoolId | null
  isBottomSheetLoginVisible: boolean
  authStep: AuthStep
  whiteLabelUser: User | null
  onAuthSuccessLandingUrl?: string
  susiHistoryState: SusiHistoryState
}

export const INIT_STATUS = 'init'

/* eslint-disable jsdoc/require-jsdoc */
export const initialState: StateType = {
  isPasswordlessLoginVisible: false,
  isUserResending: false,
  error: {},
  status: INIT_STATUS,
  userPhoneNumber: null,
  userId: null,
  flow: null,
  unlockHotelId: null,
  shouldSeeOffersUnlocked: false,
  isAuthenticated: false,
  user: null,
  isLoading: true,
  userPoolId: null,
  sourceComponent: null,
  isBottomSheetLoginVisible: false,
  authStep: 'SignInStep',
  whiteLabelUser: null,
  susiHistoryState: {
    active: false,
    analyticsData: undefined
  }
}

const {actions, reducer} = createSlice<StateType, SliceCaseReducers<StateType>>(
  {
    name: 'authentication',
    initialState,
    reducers: {
      showPasswordlessLogin: (
        state: StateType,
        action: PayloadAction<ShowPasswordlessLoginPayload>
      ) => {
        if (action.payload) {
          state.isPasswordlessLoginVisible =
            action.payload.isPasswordlessLoginVisible
          state.flow = action.payload.flow
          state.unlockHotelId = action.payload.unlockHotelId
          state.sourceComponent = action.payload.sourceComponent
          state.status = action.type
        }
      },
      hidePasswordlessLogin: (state: StateType) => {
        state.isPasswordlessLoginVisible = false
        state.flow = null
        state.unlockHotelId = null
        state.sourceComponent = null
        state.status = INIT_STATUS
      },
      cleanUpLoginErrors: (state: StateType) => {
        state.error = {}
      },
      identifyAuthenticatedUser: (
        state: StateType,
        action: PayloadAction<ClaimsType>
      ) => {
        state.userId = action.payload?.sub
      },
      setShouldSeeOffersUnlocked: (
        state: StateType,
        action: PayloadAction<boolean>
      ) => {
        state.shouldSeeOffersUnlocked = action.payload
      },
      setIsAuthenticated: (
        state: StateType,
        action: PayloadAction<AuthenticatedUser>
      ) => {
        state.isAuthenticated = action.payload.isAuthenticated
      },
      setUser: (state: StateType, action: PayloadAction<UserAction>) => {
        state.user = action.payload.user
        state.isAuthenticated = action.payload.isAuthenticated
        state.shouldSeeOffersUnlocked = action.payload.shouldSeeOffersUnlocked
        state.userPoolId = action.payload.userPoolId
        persistCognitoUserTierOnCookie(action.payload.user)
        removeUnnecessaryCookies()
      },
      setWhiteLabelUser: (state: StateType, action: PayloadAction<User>) => {
        state.whiteLabelUser = action.payload
      },
      setCustomAppData: (
        state: StateType,
        action: PayloadAction<{attributes: UserAttributes}>
      ) => {
        state.user = mergeDeepRight(state.user as User, action.payload)
      },
      setIsLoading: (state: StateType, action: PayloadAction<boolean>) => {
        state.isLoading = action.payload
      },
      hideBottomSheetLogin: (state: StateType) => {
        state.isBottomSheetLoginVisible = false
        state.status = INIT_STATUS
        state.authStep = initialState.authStep
      },
      logOut: (state: StateType) => {
        state.user = null
        state.isAuthenticated = false
        state.userPoolId = null
        state.authStep = initialState.authStep
      },
      setUnlockHotelId: (
        state: StateType,
        action: PayloadAction<StateType['unlockHotelId']>
      ) => {
        state.unlockHotelId = action.payload
      },
      setSusiState: (
        state: StateType,
        action: PayloadAction<SusiHistoryState>
      ) => {
        state.susiHistoryState = action.payload
      },
      updateSusiState: (
        state: StateType,
        action: PayloadAction<Partial<SusiHistoryState>>
      ) => {
        state.susiHistoryState = {
          ...state.susiHistoryState,
          ...action.payload
        }
      },
      resetSusiState: (state: StateType) => {
        state.susiHistoryState = initialState.susiHistoryState
      }
    },
    extraReducers: builder => {
      builder
        .addCase(
          makeSocialLogin.rejected.type,
          (state: StateType, action: PayloadAction<CognitoError>) => {
            state.error = {
              errorType: 'SocialLogin',
              errorMessage: action.payload?.name
            }

            state.status = action.type
          }
        )
        .addCase(
          upgradeUserToPlus.fulfilled.type,
          (state: StateType, action: PayloadAction<string>) => {
            if (state.user) {
              state.user.attributes['custom:app_data'] = action.payload
            }
            persistCognitoUserTierOnCookie(state.user)
          }
        )
        .addMatcher(isFulfilled(updateUserAttributes), (state, action) => {
          state.lastAction = action.type
          state.user = {
            ...state.user,
            attributes: {...state.user?.attributes, ...action.payload}
          }
          state.error = {}
        })
        .addMatcher(isPending(updateUserAttributes), (state, action) => {
          state.lastAction = action.type
          state.error = {}
        })
        .addMatcher(isRejected(updateUserAttributes), (state, action) => {
          state.lastAction = action.type
          state.error = {
            errorType: 'profileUpdate',
            // @ts-expect-error - payload could be an error (== unknown) or array of rejectedValues, updateUserAttributes types should be improved
            errorMessage: action?.payload?.message
          }
        })
    }
  }
)

/* eslint-disable jsdoc/require-jsdoc */
export const {
  showPasswordlessLogin,
  setMessageAttemptsHistory,
  identifyAuthenticatedUser,
  setShouldSeeOffersUnlocked,
  setIsAuthenticated,
  setUser,
  setCustomAppData,
  setIsLoading,
  setAuthStep,
  setUnlockHotelId,
  setWhiteLabelUser,
  setSusiState,
  updateSusiState
} = actions

/**
 * Actions for the authentication slice
 */
export const {
  hidePasswordlessLogin,
  cleanUpLoginErrors,
  setResendMessage,
  hideBottomSheetLogin,
  logOut,
  resetSusiState
  /* eslint-disable jsdoc/require-jsdoc */
} = actions as Record<
  string,
  ActionCreatorWithoutPayload<`${string}/${string}`>
>

export default reducer
